import React, {
    Fragment,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme, Text } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { SidebarContext } from './SidebarContext';
import Icon from 'shards/Imports/icons/Icon';

import styles from '../styles/MenuItem';
const useStyles = createUseStyles(styles, 'MenuItem');

const animatedItems = {};

const MenuItem = memo(
    ({
        className: classNameProp,
        isActive,
        isNewTab,
        onClick,
        hasBadge,
        badgeContent,
        hasTrial,
        icon = '',
        isNew = false,
        link = '',
        navigate = null,
        name = '',
        onMouseEnter = () => {},
        onMouseLeave = () => {},
    }) => {
        const menuItemRef = useRef();
        const prevBadgeContent = useRef(null);
        const [shouldAnimateStatic, setShouldAnimateStatic] = useState(() => {
            if (animatedItems[link]) {
                return false;
            }
            if (isNew || hasTrial) {
                animatedItems[link] = true;
                return true;
            }
            return false;
        });
        const [shouldAnimateBadge, setShouldAnimateBadge] = useState(false);
        const classes = useStyles();
        const { t } = useWeb5Context();
        const { isExpanded } = useContext(SidebarContext);
        const theme = useTheme();

        useEffect(() => {
            if (hasBadge && badgeContent && parseInt(badgeContent, 10) > 0) {
                if (prevBadgeContent.current !== badgeContent) {
                    setShouldAnimateBadge(true);
                    prevBadgeContent.current = badgeContent;
                    const timer = setTimeout(() => setShouldAnimateBadge(false), 1000);
                    return () => clearTimeout(timer);
                }
            }
        }, [hasBadge, badgeContent]);

        const rootClassName = classnames(
            classes.root,
            {
                [classes.isActive]: isActive,
                [classes.isExpanded]: isExpanded,
            },
            classNameProp,
        );

        const handleMouseEnter = useCallback(() => {
            const elTop = menuItemRef.current.getBoundingClientRect().top;
            onMouseEnter && onMouseEnter({ elTop, name });
        }, [name, onMouseEnter]);

        const handleMouseLeave = useCallback(() => {
            onMouseLeave && onMouseLeave();
        }, [onMouseLeave]);

        const handleOnClick = useCallback(
            (route) => (e) => {
                e.preventDefault();
                const url = `${window.location.origin}${route}`;
                if (e.ctrlKey || e.metaKey || e.button === 1 || e.shiftKey) {
                    window.open(url, '_blank');
                    return;
                }

                onClick && onClick();
                if (navigate) return navigate(e);
            },
            [onClick, navigate],
        );

        const menuItemChild = useMemo(
            () => (
                <Fragment>
                    <Icon name={icon} className={classes.icon} color={theme.colors.primary.white} />
                    <span
                        className={classnames(classes.label, {
                            [classes.hasBadge]: isNew,
                            [classes.hasTrial]: hasTrial,
                        })}
                    >
                        {name}
                    </span>
                    {isNew && (
                        <Text
                            type="caption"
                            className={classnames(classes.newBadge, {
                                [classes.shouldAnimate]: shouldAnimateStatic,
                            })}
                        >
                            {t('label_sidebar_new')}
                        </Text>
                    )}
                    {((hasBadge && !!parseInt(badgeContent, 10)) || (!isExpanded && hasTrial)) && (
                        <span
                            className={classnames(classes.notificationBadge, {
                                [classes.shouldAnimate]: shouldAnimateBadge,
                            })}
                        ></span>
                    )}
                    {isExpanded && hasTrial && (
                        <Icon
                            name="trialIcon"
                            className={classnames(classes.trialIcon, {
                                [classes.shouldAnimate]: shouldAnimateStatic,
                            })}
                            color="currentColor"
                        />
                    )}
                </Fragment>
            ),
            [
                badgeContent,
                classes.hasBadge,
                classes.hasTrial,
                classes.icon,
                classes.label,
                classes.newBadge,
                classes.notificationBadge,
                classes.shouldAnimate,
                classes.trialIcon,
                hasBadge,
                hasTrial,
                icon,
                isExpanded,
                isNew,
                name,
                shouldAnimateBadge,
                shouldAnimateStatic,
                t,
                theme.colors.primary.white,
            ],
        );

        const menuItemProps = useMemo(() => {
            let itemProps = {
                href: link,
                className: classnames(rootClassName, { [classes.isActive]: isActive }),
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                ref: menuItemRef,
                'aria-describedby': 'fmMenuTooltip',
                target: isNewTab ? '_blank' : undefined,
            };

            // We need this to use navigate instead of link to be able to allow block navegation
            if (!isNewTab && navigate) {
                itemProps.onClick = handleOnClick(link);
            }

            return itemProps;
        }, [
            link,
            rootClassName,
            classes.isActive,
            isActive,
            handleMouseEnter,
            handleMouseLeave,
            isNewTab,
            navigate,
            handleOnClick,
        ]);

        return <a {...menuItemProps}>{menuItemChild}</a>;
    },
);

MenuItem.propTypes = {
    className: PropTypes.string,
    isActive: PropTypes.bool.isRequired,
    isNew: PropTypes.bool,
    link: PropTypes.string.isRequired,
    navigate: PropTypes.func,
    name: PropTypes.string.isRequired,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
};

export default MenuItem;
